import { getSettingEnableNewNAMCommissionModelWebRetailer } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingEnableNewNAMCommissionModelWebRetailer";
import supportArticle from "@faire/web-api--source/routes/www/support/articles/articleId";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import {
  DEPRECATED_backwardsCompatibleValues,
  StrictLocalizeFunction,
} from "@faire/web/common/localization/localize";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { EmbeddedLink } from "@faire/web/ui/EmbeddedLink";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { type JSX } from "react";
import styled from "styled-components";

export interface IIntroProps {
  eventKey: string;
  header: string;
  description: string | JSX.Element;
  richContent: JSX.Element;
  reverse?: boolean;
  index: number;
  startTracking: boolean;
}

const RichContentImage = styled.img`
  height: 330px;
  width: 446px;

  ${responsiveMediaQueries.mobile} {
    height: 205px;
    width: 250px;
  }

  ${responsiveMediaQueries.tablet} {
    height: 220px;
    width: 290px;
  }
`;

const paymentsOnTimeDescription = (
  strictLocalize: StrictLocalizeFunction,
  isEurope: boolean
) => {
  if (getSettingEnableNewNAMCommissionModelWebRetailer()) {
    return (
      <StrictLocalMsg
        id="cAlvMJ"
        defaultMessage="We manage payment processing, pay you directly, and cover 60-day payment terms for your eligible customers."
        description={{
          text: "Describes how Faire helps to manage payments",
          img: "https://cdn.faire.com/fastly/67d6f89372d336df2ee411731f2ead9ab63ba1652e5963c9b8ee39d156480c50.png",
        }}
      />
    );
  }
  if (isEurope) {
    return (
      <StrictLocalMsg
        id="8ZxKCK"
        defaultMessage="We take care of all credit card and payment processing, and pay you directly for every order. <TextLink>How Payouts Work</TextLink>"
        values={DEPRECATED_backwardsCompatibleValues({
          TextLink: (chunk: string) => {
            return (
              <EmbeddedLink linkTo={supportArticle("360040446591")}>
                {chunk}
              </EmbeddedLink>
            );
          },
        })}
      />
    );
  }
  return (
    <>
      {strictLocalize({
        id: "RHzjne",
        defaultMessage:
          "We take care of payment processing, pay you directly, and cover 60-day payment terms for your customers.",
      })}
    </>
  );
};

export const getIntros = (
  isSSR: boolean,
  strictLocalize: StrictLocalizeFunction,
  isEurope: boolean
): IIntroProps[] => [
  {
    eventKey: "grow-business",
    header: strictLocalize({
      id: "KZ9cWQ",
      defaultMessage: "New wholesale customers",
      description: {
        text: "Marketing phrase to promote Faire to brands. Brands will have access to international customers by joining Faire",
      },
    }),
    description: strictLocalize({
      id: "1bTcgH",
      defaultMessage:
        "Boost your business with exposure to hundreds of thousands of verified independent retailers worldwide.",
      description: {
        text: "Marketing phrase to let brands know Faire has hundreds of thousands of retailers",
        img: "https://cdn.faire.com/fastly/3aee1ef0682d964173835cfd1007ab77e9deb2bacf520228343398682f91be3a.png",
      },
    }),
    richContent: (
      <RichContentImage
        src={optimizeImageUrl(
          {
            url: "https://cdn.faire.com/fastly/8a4633a1b6b3f7e5caa2dbda0c4cf89c1fd425ecf230e285713416411630f5fc.png",
          },
          isSSR
        )}
      />
    ),
    reverse: false,
    index: 1,
    startTracking: false,
  },
  {
    eventKey: "get-payments-on-time",
    header: getSettingEnableNewNAMCommissionModelWebRetailer()
      ? strictLocalize({
          id: "kTMMpe",
          defaultMessage: "Secure, on-time payments",
          description: {
            text: "Marketing phrase to promote Faire to brands",
            img: "https://cdn.faire.com/fastly/67d6f89372d336df2ee411731f2ead9ab63ba1652e5963c9b8ee39d156480c50.png",
          },
        })
      : strictLocalize({
          id: "CKkprR",
          defaultMessage: "Secure, on-time payment—guaranteed",
          description: { text: "Marketing phrase to promote Faire to brands" },
        }),
    description: paymentsOnTimeDescription(strictLocalize, isEurope),
    richContent: (
      <RichContentImage
        src={optimizeImageUrl(
          {
            url: "https://cdn.faire.com/fastly/d9f62dd87b40bfaab32024baf07d1ca7323d56d1a89e64d1555d443d8287ae17.png",
          },
          isSSR
        )}
      />
    ),
    reverse: true,
    index: 2,
    startTracking: false,
  },
  {
    eventKey: "get-more-sales",
    header: strictLocalize({
      id: "jKm6oK",
      defaultMessage: "Saved time and money",
      description: {
        text: "Marketing phrase to promote Faire to brands",
      },
    }),
    description: strictLocalize({
      id: "z7TmTn",
      defaultMessage:
        "Grow efficiently with easy order management, plus free marketing and customer tools.",
    }),
    richContent: (
      <RichContentImage
        src={optimizeImageUrl(
          {
            url: "https://cdn.faire.com/fastly/9dadca88996af66e8ffea83bbc4d8cafbbe90a755612f559ff3d06423711cd41.png",
          },
          isSSR
        )}
      />
    ),
    reverse: false,
    index: 3,
    startTracking: false,
  },
];
